import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import type { Partner, Email, Deal, AttachmentMeta, Contact } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import { clsx, AvatarStack, HeatMeter, Metric } from "./Primitives";

/* ---- Inline icons used only by Inbox ---- */

function Spark({ size = 16, w = 1.7, style }: { size?: number; w?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M12 3l1.7 4.8L18.5 9.5 13.7 11.2 12 16l-1.7-4.8L5.5 9.5l4.8-1.7z" />
      <path d="M18.5 15.5l.7 2 .8-2 .8.7-2 .7 2 .8-.8.8-.8-2-.7 2z" opacity={0.7} />
    </svg>
  );
}

function Lock({ size = 15, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x={5} y={11} width={14} height={9} rx={2} />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/* ---- Helpers ---- */

interface InboxProps {
  go: (screen: string, id?: string) => void;
  bump: () => void;
  partners: Record<string, Partner>;
  myId?: string;
  focusId?: string | null;   // open this email (e.g. from an account's communications)
  /** Phone: the list, or one email as its own screen. */
  phone?: boolean;
}

type FilterTab = "review" | "all" | "sent" | "routed";


function senderColor(e: Email): string {
  // Derive color from the org/role context on the email
  const role = (e.from_role || "").toLowerCase();
  if (role.includes("operator")) return "#5b6b7d";
  if (role.includes("noc")) return "#2D7D9A";
  if (role.includes("regulator")) return "#9A3F5B";
  if (role.includes("government")) return "#3B5B92";
  if (role.includes("dfi") || role.includes("eca")) return "#0E7C66";
  // Fallback: hash the org name for a stable color
  const org = e.from_org || "";
  if (!org) return "#8a93a0";
  let hash = 0;
  for (let i = 0; i < org.length; i++) hash = ((hash << 5) - hash + org.charCodeAt(i)) | 0;
  const colors = ["#5b6b7d", "#2D7D9A", "#9A3F5B", "#3B5B92", "#0E7C66", "#8a93a0"];
  return colors[Math.abs(hash) % colors.length];
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("");
}

function fmtWhen(iso: string | null | undefined): { day: string; time: string } {
  if (!iso) return { day: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { day: "—", time: "" };
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return { day, time };
}

function needsReview(e: Email): boolean {
  return e.status === "auto" || e.status === "unallocated";
}

/* ---- AllocChip (inline pill in the list row) ---- */

function AllocChip({ e, deal }: { e: Email; deal: Deal | null }) {
  if (e.status === "private")
    return <span className="alloc-chip private"><Lock size={11} />Private</span>;
  if (e.status === "unallocated")
    return <span className="alloc-chip unrouted">Unrouted</span>;
  if (!deal && e.category && (e.status === "manual" || e.status === "confirmed"))
    return <span className="alloc-chip set">{I.check({ size: 11, w: 2.4 })}{e.category}</span>;
  return (
    <span className={clsx("alloc-chip", e.status === "auto" ? "auto" : "set")}>
      {e.status === "auto"
        ? <Spark size={11} w={2} />
        : I.check({ size: 11, w: 2.4 })}
      {deal ? deal.name : "Routed"}
    </span>
  );
}

/* ---- AllocCard (right-pane AI allocation card) ---- */

function AllocCard({
  e,
  deal,
  deals,
  picker,
  setPicker,
  confirm,
  reallocate,
  keepPrivate,
  retract,
  createDeal,
  go,
  partners,
}: {
  e: Email;
  deal: Deal | null;
  deals: Deal[];
  picker: boolean;
  setPicker: React.Dispatch<React.SetStateAction<boolean>>;
  confirm: (id: string) => void;
  reallocate: (id: string, dealId: string | null) => void;
  keepPrivate: (id: string) => void;
  retract: (id: string) => void;
  createDeal: (emailId: string) => void;
  go: (screen: string, id?: string) => void;
  partners: Record<string, Partner>;
}) {
  const conf = Math.round((e.ai_confidence ?? 0) * 100);
  const confClass = conf >= 80 ? "hi" : conf >= 50 ? "mid" : "lo";

  const DealPicker = picker && (
    <div className="deal-picker">
      <div className="dp-label">Allocate to a project</div>
      <div className="dp-list">
        {deals.map((d) => (
          <button key={d.id} className={clsx("dp-item", e.deal_id === d.id && "cur")}
            onClick={() => reallocate(e.id, d.id)}>
            <HeatMeter level={d.heat} />
            <span className="dp-name">{d.name}</span>
            <span className="dp-mkt">{d.market}</span>
            {e.deal_id === d.id && I.check({ size: 14, w: 2.2, style: { color: "var(--positive)" } })}
          </button>
        ))}
      </div>
      <button className="dp-new" onClick={() => createDeal(e.id)}>{I.plus({ size: 14 })}Create new deal from this email</button>
    </div>
  );

  /* header varies by status */
  let head: React.ReactNode;
  let tone: string;
  if (e.status === "auto") {
    tone = "auto";
    head = <><Spark size={15} w={1.8} /><span>Saga AI routed this email</span></>;
  } else if (e.status === "unallocated") {
    tone = "warn";
    head = <><Spark size={15} w={1.8} /><span>Saga AI couldn't confidently route this</span></>;
  } else if (e.status === "private") {
    tone = "private";
    head = <><Lock size={14} /><span>Private to you &mdash; not shared on any project</span></>;
  } else {
    tone = "set";
    head = (
      <>
        {I.check({ size: 15, w: 2.4 })}
        <span>
          {e.status === "confirmed"
            ? "Confirmed — shared with the team"
            : "Allocated by you — shared with the team"}
        </span>
      </>
    );
  }

  const routedBy = e.routed_by && e.routed_by !== "ai" ? partners[e.routed_by] : null;

  return (
    <div className={clsx("alloc-card", "ac-" + tone)}>
      <div className="ac-head">
        {head}
        {routedBy && <span className="ac-by">by {routedBy.name.split(" ")[0]}</span>}
      </div>

      {/* routed target + link */}
      {e.deal_id && deal && (
        <button className="ac-target" onClick={() => go("project", e.deal_id!)}>
          <HeatMeter level={deal.heat} />
          <div className="ac-target-body">
            <div className="ac-target-name">{deal.name}</div>
            <div className="ac-target-rep">
              {deal.principal_name} &rarr; {deal.operator_name} &middot; {deal.market}
            </div>
          </div>
          {I.arrowUpRight({ size: 15, className: "ac-target-go" })}
        </button>
      )}

      {/* confidence + reason for AI states */}
      {(e.status === "auto" || e.status === "unallocated") && (
        <div className="ac-ai">
          <div className="ac-conf">
            <span className="ac-conf-label">AI confidence</span>
            <span className={clsx("ac-conf-bar", confClass)}>
              <span style={{ width: conf + "%" }} />
            </span>
            <span className={clsx("ac-conf-n mono", confClass)}>{conf}%</span>
          </div>
          {e.ai_reason && <p className="ac-reason">{e.ai_reason}</p>}
        </div>
      )}

      {DealPicker}

      {/* actions */}
      <div className="ac-actions">
        {e.status === "auto" && (
          <>
            <button className="btn primary sm" onClick={() => confirm(e.id)}>
              {I.check({ size: 14, w: 2.2 })}Confirm routing
            </button>
            <button className="btn ghost sm" onClick={() => setPicker((v) => !v)}>
              Reallocate
            </button>
            <button className="btn ghost sm" title="Saga's suggestion is wrong — file it without a deal" onClick={() => reallocate(e.id, null)}>
              No deal
            </button>
            <button className="btn ghost sm" title="Hide it from the other partners" onClick={() => keepPrivate(e.id)}>
              {I.plus({ size: 13, style: { transform: "rotate(45deg)" } })} Keep private
            </button>
          </>
        )}
        {e.status === "unallocated" && (
          <>
            <button className="btn primary sm" onClick={() => setPicker((v) => !v)}>
              {I.pipeline({ size: 14, w: 1.8 })}Allocate to project
            </button>
            <button className="btn ghost sm" title="Not about a deal — file it without one" onClick={() => reallocate(e.id, null)}>
              No deal
            </button>
            <button className="btn ghost sm" title="Hide it from the other partners" onClick={() => keepPrivate(e.id)}>
              {I.plus({ size: 13, style: { transform: "rotate(45deg)" } })} Keep private
            </button>
          </>
        )}
        {(e.status === "confirmed" || e.status === "manual") && (
          <>
            <button className="btn ghost sm" onClick={() => setPicker((v) => !v)}>
              Reallocate
            </button>
            <button className="btn ghost sm" onClick={() => retract(e.id)}>
              {I.arrowRight({ size: 13, style: { transform: "rotate(180deg)" } })}Retract
            </button>
            <button className="btn ghost sm" onClick={() => keepPrivate(e.id)}>
              {I.plus({ size: 13, style: { transform: "rotate(45deg)" } })} Keep private
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- What this email is linked to: deal suggestion, related tasks, tasks from it ---- */

interface Links {
  deal: { deal_id: string; deal_name: string; conf: number; reason: string } | null;
  tasks: { id: string; task: string; owner: string; status: string; due: string | null; external_code: string | null;
    waiting_on: string | null; reply_email_id: string | null; deal_name: string | null; same_people: boolean }[];
}

function EmailLinks({ e, go, reallocate }: { e: Email; go: (s: string, id?: string) => void; reallocate: (id: string, dealId: string) => void }) {
  const { data, refetch } = useData<Links>(`/api/emails/${e.id}/links`);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setMsg(null); }, [e.id]);
  if (!data) return null;
  const showDeal = data.deal && !e.deal_id && data.deal.conf < 0.65;
  return (
    <div className="em-links">
      {showDeal && (
        <div className="em-link-row">
          <span className="em-link-k">Maybe</span>
          <span className="em-link-v"><b>{data.deal!.deal_name}</b> — {data.deal!.reason}</span>
          <button className="btn ghost sm" onClick={() => reallocate(e.id, data.deal!.deal_id)}>File under it</button>
        </div>
      )}
      {data.tasks.length > 0 && (
        <div className="em-link-row top">
          <span className="em-link-k">Tasks</span>
          <span className="em-link-v">
            {data.tasks.map(t => (
              <button key={t.id} className="em-task" onClick={() => go("tasks", t.id)}>
                {t.reply_email_id === e.id ? "✉ answers: " : t.status === "waiting" ? "⏳ " : ""}
                {t.external_code ? <b>{t.external_code} </b> : null}{t.task.replace(/^[FS]\d[^:]*:\s*/, "").slice(0, 80)}
              </button>
            ))}
          </span>
        </div>
      )}
      <div className="em-link-row">
        <span className="em-link-k" />
        <button className="ld-link" disabled={busy} onClick={async () => {
          setBusy(true); setMsg(null);
          try {
            const r = await api.post<{ created: unknown[]; duplicates: unknown[] }>("/api/todos/extract", {
              text: `From: ${e.from_name || ""} <${e.from_email || ""}>\nSubject: ${e.subject || ""}\n\n${e.body || e.preview || ""}`,
              source_type: "email", source_id: e.id, source_label: `Email: ${e.subject || "(no subject)"}`, deal_id: e.deal_id || null,
            });
            setMsg(r.created.length ? `${r.created.length} suggested task${r.created.length === 1 ? "" : "s"} added (Tasks → Suggested)`
              : r.duplicates.length ? "Nothing new — those are already tasks" : "No tasks found in this email");
            refetch();
          } catch (err) { setMsg(err instanceof Error ? err.message : String(err)); } finally { setBusy(false); }
        }}>{busy ? "Reading…" : "✦ Find tasks in this email"}</button>
        {msg && <span className="em-link-msg">{msg}</span>}
      </div>
    </div>
  );
}

/* ---- Category (alongside the project an email is filed under) ---- */

const EMAIL_CATEGORIES = ["Project", "Principal", "Prospect", "Network", "Market intel", "Admin & finance", "Events", "Other"];

function CategoryRow({ e }: { e: Email }) {
  const [cat, setCat] = useState(e.category || "");
  const [err, setErr] = useState(false);
  useEffect(() => { setCat(e.category || ""); setErr(false); }, [e.id, e.category]);
  const change = async (v: string) => {
    const before = cat;
    setCat(v); setErr(false);
    try {
      await api.put(`/api/emails/${e.id}/category`, { category: v || null });
      e.category = v || null;   // keep the cached list row in step until the next refetch
    }
    catch { setCat(before); setErr(true); }
  };
  return (
    <div className="read-cat">
      <span className="read-cat-label">Category</span>
      <select value={cat} onChange={(ev) => change(ev.target.value)}>
        <option value="">—</option>
        {EMAIL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {err && <span className="read-cat-err">Couldn't save — try again</span>}
    </div>
  );
}

/* ---- Attachment helpers ---- */

function fmtSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function fileIcon(mime: string): string {
  if (mime.startsWith("image/")) return "\u{1F5BC}";
  if (mime.includes("pdf")) return "\u{1F4C4}";
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime.includes("csv")) return "\u{1F4CA}";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "\u{1F4CA}";
  if (mime.includes("word") || mime.includes("document")) return "\u{1F4DD}";
  if (mime.includes("zip") || mime.includes("compressed")) return "\u{1F4E6}";
  return "\u{1F4CE}";
}

function AttachmentList({ emailId, attachments }: { emailId: string; attachments: AttachmentMeta[] }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="read-attachments">
      <div className="read-attach-label">{attachments.length} attachment{attachments.length > 1 ? "s" : ""}</div>
      <div className="read-attach-list">
        {attachments.map((a, i) => (
          <a
            key={i}
            className="read-attach-chip"
            href={`/api/emails/${emailId}/attachments/${i}`}
            target="_blank"
            rel="noreferrer"
            title="Open attachment"
          >
            <span className="read-attach-icon">{fileIcon(a.mimeType)}</span>
            <span className="read-attach-name">{a.filename}</span>
            <span className="read-attach-size">{fmtSize(a.size)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ---- EmailBody (HTML iframe or plain text fallback) ---- */

export function EmailBody({ email }: { email: Email }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observer = useRef<ResizeObserver | null>(null);

  // Size the frame to the whole email so the reading pane scrolls as one page,
  // and keep it fitted as images load or the window is resized.
  const fit = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc?.documentElement) return;
    iframe.style.height = Math.max(doc.documentElement.scrollHeight, 120) + "px";
  }, []);

  const onLoad = useCallback(() => {
    observer.current?.disconnect();
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;
    fit();
    observer.current = new ResizeObserver(fit);
    observer.current.observe(doc.body);
    doc.querySelectorAll("img").forEach(img => img.addEventListener("load", fit));
  }, [fit]);

  useEffect(() => () => observer.current?.disconnect(), []);
  useEffect(() => {
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  if (email.body_html) {
    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
html{overflow-y:hidden;}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#222;margin:0;padding:0 0 8px;word-wrap:break-word;overflow-x:auto;}
img{max-width:100%;height:auto;}
a{color:#1a73e8;}
blockquote{border-left:3px solid #ddd;margin:8px 0;padding:4px 12px;color:#555;}
table{border-collapse:collapse;max-width:100%;}
td,th{padding:4px 8px;}
pre{white-space:pre-wrap;font-size:13px;background:#f5f5f5;padding:8px;border-radius:4px;}
.gmail_quote{color:#666;}
</style></head><body>${email.body_html}</body></html>`;

    return (
      <iframe
        ref={iframeRef}
        srcDoc={doc}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        onLoad={onLoad}
        className="read-body-frame"
        title="Email content"
      />
    );
  }

  return (
    <div className="read-body">
      {(email.body || "No content").split("\n").map((p, i) =>
        p.trim()
          ? <p key={i}>{p}</p>
          : <div key={i} style={{ height: 8 }} />
      )}
    </div>
  );
}

/* ---- ComposeModal (new email from the Inbox) ---- */

function ComposeModal({ close, onSent }: { close: () => void; onSent: () => void }) {
  const { data: contacts } = useData<Contact[]>("/api/contacts");
  const [to, setTo] = useState("");
  const [showSug, setShowSug] = useState(false);
  const [cc, setCc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setTimeout(() => toRef.current?.focus(), 60); }, []);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [close]);

  const q = to.trim().toLowerCase();
  const suggestions = (showSug && q.length >= 1 && contacts ? contacts : [])
    .filter(c => c.email && (
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.org_name || "").toLowerCase().includes(q)
    ))
    .slice(0, 6);

  const validTo = to.includes("@");

  const send = async () => {
    if (!validTo || !body.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post("/api/emails/send", {
        to: to.trim(),
        cc: cc.split(",").map(a => a.trim()).filter(a => a.includes("@")),
        subject: subject || "(no subject)",
        body: body.trim(),
      });
      setSent(true);
      onSent();
      setTimeout(close, 850);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send.");
      setSending(false);
    }
  };

  return (
    <div className="ai-overlay" onMouseDown={close}>
      <div className="compose-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="compose-head">
          <div className="compose-title">New email</div>
          <span className="cmd-esc">esc</span>
        </div>
        <div className="compose-to" style={{ position: "relative" }}>
          <span className="compose-to-label">To</span>
          <input
            ref={toRef}
            className="cmp-to-input"
            placeholder="Name, company or email address…"
            value={to}
            onChange={(e) => { setTo(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
          />
          {!showCc && (
            <button className="ir-cc-toggle" onClick={() => setShowCc(true)}>+ Cc</button>
          )}
          {suggestions.length > 0 && (
            <div className="cmp-sug">
              {suggestions.map(c => (
                <button key={c.id} className="cmp-sug-item"
                  onMouseDown={(e) => { e.preventDefault(); setTo(c.email); setShowSug(false); }}>
                  <span className="cmp-sug-name">{c.name}</span>
                  <span className="cmp-sug-mail">{c.email}</span>
                  {c.org_name && <span className="cmp-sug-org">{c.org_name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {showCc && (
          <input
            className="ir-cc"
            placeholder="Cc — comma-separated addresses"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            disabled={sent}
          />
        )}
        <input
          className="compose-subject"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          className="compose-body"
          rows={8}
          placeholder="Write your message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
          disabled={sent}
        />
        {error && <div className="compose-error">{error}</div>}
        <div className="compose-foot">
          {sent ? (
            <span className="compose-sent">{I.check({ size: 14, w: 2.4 })} Sent</span>
          ) : (
            <>
              <button className="btn ghost sm" onClick={close} disabled={sending}>Cancel</button>
              <button className="btn primary sm" onClick={send} disabled={!validTo || !body.trim() || sending}>
                {sending ? "Sending…" : <>{I.send({ size: 14 })} Send</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- ReadingPane (right panel) ---- */

function InlineReply({ email, onDone }: { email: Email; onDone: () => void }) {
  const [body, setBody] = useState("");
  const [cc, setCc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setTimeout(() => ref.current?.focus(), 40); }, []);

  const subject = /^re:/i.test(email.subject || "")
    ? (email.subject || "")
    : `Re: ${email.subject || ""}`.trim();

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post("/api/emails/send", {
        to: email.from_email,
        cc: cc.split(",").map(a => a.trim()).filter(a => a.includes("@")),
        subject,
        body: body.trim(),
        reply_to_email_id: email.id,
      });
      setSent(true);
      setTimeout(onDone, 900);
    } catch {
      setError("Failed to send — check that your Gmail is connected.");
      setSending(false);
    }
  };

  return (
    <div className="inline-reply">
      <div className="ir-head">
        {I.send({ size: 14, w: 1.8 })}
        <span className="ir-to">To: {email.from_name || email.from_email}</span>
        <span className="ir-subj mono">{subject}</span>
        {!showCc && (
          <button className="ir-cc-toggle" onClick={() => setShowCc(true)}>+ Cc</button>
        )}
      </div>
      {showCc && (
        <input
          className="ir-cc"
          placeholder="Cc — comma-separated addresses"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          disabled={sent}
        />
      )}
      <textarea
        ref={ref}
        className="ir-body"
        rows={4}
        placeholder="Write your reply…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
        disabled={sent}
      />
      {error && <div className="ir-error">{error}</div>}
      <div className="ir-foot">
        {sent ? (
          <span className="ir-sent">{I.check({ size: 13, w: 2.4 })} Sent — threaded on the conversation</span>
        ) : (
          <>
            <button className="btn ghost sm" onClick={onDone} disabled={sending}>Cancel</button>
            <button className="btn primary sm" onClick={send} disabled={sending || !body.trim()}>
              {sending ? "Sending…" : <>{I.send({ size: 13 })} Send reply</>}
            </button>
            <span className="ir-hint mono">⌘↵</span>
          </>
        )}
      </div>
    </div>
  );
}

function ReadingPane({
  e,
  deals,
  picker,
  setPicker,
  confirm,
  reallocate,
  keepPrivate,
  retract,
  createDeal,
  go,
  partners,
}: {
  e: Email;
  deals: Deal[];
  picker: boolean;
  setPicker: React.Dispatch<React.SetStateAction<boolean>>;
  confirm: (id: string) => void;
  reallocate: (id: string, dealId: string | null) => void;
  keepPrivate: (id: string) => void;
  retract: (id: string) => void;
  createDeal: (emailId: string) => void;
  go: (screen: string, id?: string) => void;
  partners: Record<string, Partner>;
}) {
  const [showReply, setShowReply] = useState(false);
  const w = fmtWhen(e.date);
  const deal = e.deal_id ? deals.find((d) => d.id === e.deal_id) || null : null;
  const shared = (e.status === "confirmed" || e.status === "manual" || e.status === "auto") && e.deal_id;

  const toIds = e.to_addresses || [];
  const canReply = !!e.from_email && !e.from_email.includes("saga-advisory.com");

  // Reset reply pane when email changes
  useEffect(() => { setShowReply(false); }, [e.id]);

  return (
    <>
      <div className="read-head">
        <h2 className="read-subj">{e.subject || "(no subject)"}</h2>
        <div className="read-meta">
          <span className="read-av" style={{ background: senderColor(e) }}>
            {initials(e.from_name || e.from_email)}
          </span>
          <div className="read-from">
            <div className="read-from-name">
              {e.from_name || e.from_email || "Unknown"}
              {e.from_role && <span className="read-from-role"> &middot; {e.from_role}</span>}
            </div>
            <div className="read-from-org">{e.from_org || "External"}</div>
          </div>
          <div className="read-when">
            <div className="mono">{w.day}</div>
            <div className="read-time mono">{w.time}</div>
          </div>
          {canReply && !showReply && (
            <button className="btn ghost sm read-reply-btn" onClick={() => setShowReply(true)}>
              {I.send({ size: 13, w: 1.8 })} Reply
            </button>
          )}
        </div>
        <div className="read-to">
          <span className="read-to-label">
            {shared ? "Shared with" : e.status === "private" ? "Private to" : "To"}
          </span>
          <AvatarStack ids={toIds} partners={partners} size={21} />
          <span className="read-to-names">
            {toIds.map((id) => {
              const p = partners[id];
              return p ? p.name.split(" ")[0] : id;
            }).join(", ")}
          </span>
        </div>
      </div>

      {/* AI ALLOCATION CARD */}
      <AllocCard
        e={e}
        deal={deal}
        deals={deals}
        picker={picker}
        setPicker={setPicker}
        confirm={confirm}
        reallocate={reallocate}
        keepPrivate={keepPrivate}
        retract={retract}
        createDeal={createDeal}
        go={go}
        partners={partners}
      />

      <EmailLinks e={e} go={go} reallocate={reallocate} />
      <CategoryRow e={e} />
      {e.sent_by_agent_name && (
        <div className="read-bot-note">{I.ai({ size: 13 })} Sent by the bot “{e.sent_by_agent_name}” on the partner's behalf</div>
      )}

      <AttachmentList emailId={e.id} attachments={e.attachments} />
      <EmailBody email={e} />

      {showReply && (
        <InlineReply email={e} onDone={() => setShowReply(false)} />
      )}

      {deal && (
        <button className="read-appears" onClick={() => go("project", deal.id)}>
          {I.pipeline({ size: 14, w: 1.8 })}
          <span>Appears on <b>{deal.name}</b></span>
          {I.arrowRight({ size: 14 })}
        </button>
      )}
    </>
  );
}

/* ---- Main Inbox Component ---- */

const WINDOW_WEEKS = 4;
const MAX_ROWS = 500;

export default function Inbox({ go, bump, partners, myId, focusId, phone }: InboxProps) {
  const [filter, setFilter] = useState<FilterTab>(focusId ? "all" : "review");
  const folderParam = filter === "all" ? "" : `&folder=${filter}`;
  // Last 4 weeks by default; "Show older" widens the window 4 weeks at a time.
  const [weeks, setWeeks] = useState(WINDOW_WEEKS);
  const { data: emails, loading: emailsLoading, error: emailsError, refetch: refetchEmails } =
    useData<Email[]>(`/api/emails?limit=${MAX_ROWS}&weeks=${weeks}${folderParam}`);
  const { data: deals } = useData<Deal[]>("/api/deals");
  const [sel, setSel] = useState<string | null>(focusId || null);
  const [picker, setPicker] = useState(false);
  const [fullEmail, setFullEmail] = useState<Email | null>(null);

  const allEmails = emails || [];
  const allDeals = deals || [];

  // Derive overrides stored locally for optimistic UI updates
  const [overrides, setOverrides] = useState<Record<string, Partial<Email>>>({});

  const view = (e: Email): Email => ({ ...e, ...overrides[e.id] });

  const firstFilter = useRef(true);
  useEffect(() => {
    if (firstFilter.current) { firstFilter.current = false; return; } // keep a linked email selected
    setWeeks(WINDOW_WEEKS);
    setSel(null);
  }, [filter]);

  const list = useMemo(() => {
    return allEmails.map(view).filter((e) => !(e as any)._deleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmails, overrides]);

  const reviewCount = filter === "review" ? list.length : allEmails.map(view).filter(needsReview).length;
  const autoRoutedCount = allEmails.map(view).filter((e) => e.routed_by === "ai" && e.deal_id).length;

  // Select first email if nothing selected yet
  const selectedId = sel || (list.length > 0 ? list[0].id : null);
  // A linked email may be outside the loaded window — show it from its own fetch.
  const baseEmail = selectedId
    ? allEmails.find((e) => e.id === selectedId) || (fullEmail?.id === selectedId ? fullEmail : null) || allEmails[0]
    : null;
  const current = baseEmail
    ? view(fullEmail?.id === baseEmail.id ? { ...baseEmail, ...fullEmail } : baseEmail)
    : null;

  // Fetch full email (with body_html) when selection changes
  useEffect(() => {
    if (!selectedId) return;
    setFullEmail(null);
    api.get<Email>(`/api/emails/${selectedId}`).then(setFullEmail).catch(() => {});
  }, [selectedId]);

  const applyOverride = (id: string, patch: Partial<Email>) => {
    setOverrides((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
    setPicker(false);
  };

  const advanceFrom = (id: string) => {
    const idx = list.findIndex((e) => e.id === id);
    if (idx >= 0 && list.length > 1) {
      const next = idx < list.length - 1 ? list[idx + 1] : list[idx - 1];
      setSel(next.id);
    }
  };

  const rollback = (id: string) => {
    setOverrides((s) => { const next = { ...s }; delete next[id]; return next; });
  };

  const confirm = async (id: string) => {
    advanceFrom(id);
    applyOverride(id, { status: "confirmed", routed_by: myId || "geir" });
    try {
      await api.put("/api/emails/" + id + "/route", { deal_id: current?.deal_id, status: "confirmed" });
      refetchEmails();
      bump();
    } catch { rollback(id); }
  };

  // dealId null = "No deal": the suggestion was wrong; the email is filed without a deal and leaves the review queue.
  const reallocate = async (id: string, dealId: string | null) => {
    advanceFrom(id);
    applyOverride(id, { status: "manual", deal_id: dealId, routed_by: myId || "geir" });
    try {
      await api.put("/api/emails/" + id + "/route", { deal_id: dealId, status: "manual" });
      refetchEmails();
      bump();
    } catch { rollback(id); }
  };

  const keepPrivate = async (id: string) => {
    advanceFrom(id);
    // Hide via the _deleted override only (the list useMemo filters it out),
    // so a failed dismiss can be rolled back.
    applyOverride(id, { _deleted: true } as any);
    try {
      await api.put("/api/emails/" + id + "/private", {});
      refetchEmails();
      bump();
    } catch { rollback(id); }
  };

  const createDealFromEmail = async (emailId: string) => {
    const em = allEmails.find((x) => x.id === emailId);
    if (!em) return;
    const name = prompt("Deal name:", em.subject || "New deal");
    if (!name) return;
    try {
      const newDeal = await api.post<Deal>("/api/deals", { name: name.trim() });
      await api.put("/api/emails/" + emailId + "/route", { deal_id: newDeal.id, status: "manual" });
      bump();
      go("project", newDeal.id);
    } catch { /* ignore */ }
  };

  const retract = async (id: string) => {
    const em = allEmails.find((x) => x.id === id);
    const sugDealId = em?.ai_deal_id || null;
    try {
      if (sugDealId) {
        applyOverride(id, { status: "auto", deal_id: sugDealId, routed_by: "ai" });
        await api.put("/api/emails/" + id + "/route", { deal_id: sugDealId, status: "auto" });
      } else {
        applyOverride(id, { status: "unallocated", deal_id: null, routed_by: "ai" });
        await api.put("/api/emails/" + id + "/route", { deal_id: null, status: "unallocated" });
      }
      refetchEmails();
      bump();
    } catch { rollback(id); }
  };

  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const syncInbox = async (clean = false) => {
    setSyncing(true);
    setSyncError(null);
    try {
      await api.post("/api/sync/gmail", { clean });
      // Poll for completion
      const poll = async (attempts: number) => {
        if (attempts <= 0) { setSyncing(false); return; }
        await new Promise(r => setTimeout(r, 2000));
        try {
          const st = await api.get<{ gmail: { running: boolean; error: string | null } }>("/api/sync/status");
          if (st.gmail.running) { poll(attempts - 1); return; }
          if (st.gmail.error) setSyncError(st.gmail.error);
          refetchEmails(); bump(); setSyncing(false);
        } catch { setSyncing(false); }
      };
      poll(clean ? 30 : 15);
    } catch { setSyncing(false); }
  };

  const runDiagnose = async () => {
    setDiagnosing(true);
    setSyncError(null);
    try {
      const results = await api.get<Array<{ email: string; authOk: boolean; messagesTotal: number | null; testQuery: { count: number } | null; historyId: string | null; error: string | null }>>("/api/sync/diagnose");
      const lines = results.map(r => {
        if (r.error) return `${r.email}: ERROR — ${r.error}`;
        const hid = r.historyId ? `hid=${r.historyId.slice(0, 8)}…` : "hid=null(backfill)";
        return `${r.email}: auth=${r.authOk ? "OK" : "FAIL"}, total=${r.messagesTotal ?? "?"}, recent=${r.testQuery?.count ?? "?"}, ${hid}`;
      });
      setSyncError(lines.join("\n"));
    } catch (err) {
      setSyncError("Diagnose failed: " + String(err));
    }
    setDiagnosing(false);
  };

  const tabs: [FilterTab, string, number | null][] = [
    ["review", "Needs review", filter === "review" ? list.length : null],
    ["all", "All", null],
    ["sent", "Sent", null],
    ["routed", "Routed", null],
  ];

  return (
    <div className="screen inbox-screen">
      <div className="page-head inbox-head">
        <div>
          <h1 className="page-title">Inbox</h1>
          <div className="page-sub">
            <Spark size={13} w={1.8} style={{ color: "var(--ember)" }} />
            Shared with all three partners and auto-routed by Saga AI &mdash; unless kept private
          </div>
        </div>
        {syncError && (
          <div className="sync-diag" style={{ whiteSpace: "pre-wrap", fontSize: 12, padding: "8px 12px", background: syncError.includes("ERROR") || syncError.includes("FAIL") ? "#fef2f2" : "#f0fdf4", border: "1px solid", borderColor: syncError.includes("ERROR") || syncError.includes("FAIL") ? "#fca5a5" : "#86efac", borderRadius: 8, marginBottom: 8, color: "#333" }}>
            {syncError}
            <button onClick={() => setSyncError(null)} style={{ marginLeft: 12, cursor: "pointer", background: "none", border: "none", color: "#999" }}>x</button>
          </div>
        )}
        <div className="inbox-stats">
          <button className="btn primary sm" onClick={() => setComposeOpen(true)} style={{ marginRight: 8 }}>
            {I.send({ size: 14 })} New email
          </button>
          <button className="btn ghost sm" onClick={() => syncInbox(false)} disabled={syncing} style={{ marginRight: 4 }}>
            {I.mail({ size: 14, w: 1.8 })} {syncing ? "Syncing…" : "Refresh"}
          </button>
          <button className="btn ghost sm" onClick={() => syncInbox(true)} disabled={syncing} style={{ marginRight: 8, fontSize: 11 }}>
            Clean sync
          </button>
          <button className="btn ghost sm" onClick={runDiagnose} disabled={diagnosing} style={{ marginRight: 8, fontSize: 11 }}>
            {diagnosing ? "Testing…" : "Diagnose"}
          </button>
          <Metric k="Need review" v={reviewCount} />
          <Metric k="Auto-routed" v={autoRoutedCount} />
          <Metric
            k="Recipients"
            v={<AvatarStack ids={["geir", "trond", "bjorn"]} partners={partners} size={24} />}
          />
        </div>
      </div>

      <div className={clsx("inbox", phone && (focusId ? "ph-show-detail" : "ph-show-list"))}>
        {/* ---- list pane ---- */}
        <div className="inbox-list">
          <div className="inbox-tabs">
            {tabs.map(([key, label, count]) => (
              <button
                key={key}
                className={clsx("ibx-tab", filter === key && "active")}
                onClick={() => setFilter(key)}
              >
                {label}
                {count != null && <span className="ibx-tab-n">{count}</span>}
              </button>
            ))}
          </div>
          <div className="inbox-rows">
            {emailsError && <div className="ibx-empty" style={{ color: "#b91c1c" }}>Failed to load emails: {emailsError}</div>}
            {!emailsError && emailsLoading && list.length === 0 && <div className="ibx-empty">Loading emails…</div>}
            {!emailsError && !emailsLoading && list.length === 0 && <div className="ibx-empty">Inbox zero. Go grab a coffee — you've earned it.</div>}
            {list.map((e) => {
              const w = fmtWhen(e.date);
              const deal = e.deal_id ? allDeals.find((d) => d.id === e.deal_id) || null : null;
              return (
                <div
                  key={e.id}
                  className={clsx("ibx-row", selectedId === e.id && "sel", e.unread && "unread")}
                  onClick={() => { if (phone) { go("inbox-all", e.id); return; } setSel(e.id); setPicker(false); }}
                >
                  <span className="ibx-av" style={{ background: senderColor(e) }}>
                    {initials(e.from_name || e.from_email)}
                  </span>
                  <div className="ibx-main">
                    <div className="ibx-line1">
                      <span className="ibx-from">{e.from_name || e.from_email || "Unknown"}</span>
                      <span className="ibx-when mono">{w.day}</span>
                    </div>
                    <div className="ibx-subj">
                      {e.attachments?.length > 0 && <span className="ibx-clip">{"\u{1F4CE}"}</span>}
                      {e.subject}
                    </div>
                    <div className="ibx-prev">{e.preview}</div>
                    <div className="ibx-foot">
                      <AllocChip e={e} deal={deal} />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="ibx-window">
              <span>
                {list.length >= MAX_ROWS ? `Showing the newest ${MAX_ROWS}` : `Showing the last ${weeks} weeks`}
              </span>
              <button className="btn ghost sm" disabled={emailsLoading} onClick={() => setWeeks(w => w + WINDOW_WEEKS)}>
                {emailsLoading ? "Loading…" : "Show older emails"}
              </button>
            </div>
          </div>
        </div>

        {composeOpen && (
          <ComposeModal
            close={() => setComposeOpen(false)}
            onSent={() => { refetchEmails(); bump(); }}
          />
        )}

        {/* ---- reading pane ---- */}
        <div className="inbox-read">
          {current && (
            <ReadingPane
              e={current}
              deals={allDeals}
              picker={picker}
              setPicker={setPicker}
              confirm={confirm}
              reallocate={reallocate}
              keepPrivate={keepPrivate}
              retract={retract}
              createDeal={createDealFromEmail}
              go={go}
              partners={partners}
            />
          )}
        </div>
      </div>
    </div>
  );
}
